import React, { useState, useEffect, useCallback } from 'react';
import {
  Calendar, Play, Pause, Trash2, Plus, X, ChevronDown, ChevronRight,
  CheckCircle, XCircle, Clock, Loader2, RefreshCw, LogOut, Bot, Activity,
  AlarmClock, AlertCircle, Terminal, Zap, Database, Mail, MailOpen, MapPin,
  Inbox, Check, X as XIcon, ExternalLink, Tag,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { format, formatDistanceToNow } from 'date-fns';

// ─── Types ─────────────────────────────────────────────────────────────────

type ScheduleType = 'agent' | 'function';

interface Schedule {
  id: string;
  name: string;
  description: string | null;
  prompt: string | null;
  cron_expression: string;
  is_active: boolean;
  last_run_at: string | null;
  next_run_at: string | null;
  created_at: string;
  schedule_type: ScheduleType;
  function_slug: string | null;
  config: Record<string, unknown> | null;
}

interface RunLog {
  id: string;
  schedule_id: string | null;
  schedule_name: string | null;
  status: 'running' | 'success' | 'error' | 'cancelled';
  prompt: string | null;
  output: string | null;
  events_inserted: number;
  error_message: string | null;
  duration_ms: number | null;
  triggered_by: string;
  started_at: string;
  completed_at: string | null;
}

interface AdminDashboardProps {
  onLogout: () => void;
  userEmail: string;
}

interface ContactSubmission {
  id: string;
  name: string;
  email: string;
  subject: string;
  message: string;
  read: boolean;
  created_at: string;
}

interface EventRequest {
  id: string;
  title: string;
  description: string | null;
  start_datetime: string | null;
  end_datetime: string | null;
  venue_name: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  url: string | null;
  image_url: string | null;
  price: string | null;
  categories: string[] | null;
  submitter_name: string | null;
  submitter_email: string | null;
  status: 'pending' | 'approved' | 'rejected';
  reviewed_at: string | null;
  reviewed_by: string | null;
  admin_notes: string | null;
  created_at: string;
}

// ─── Constants ─────────────────────────────────────────────────────────────

const CRON_PRESETS = [
  { label: 'Every hour',       value: '0 * * * *' },
  { label: 'Every 6 hours',    value: '0 */6 * * *' },
  { label: 'Every 12 hours',   value: '0 */12 * * *' },
  { label: 'Daily at 8am',     value: '0 8 * * *' },
  { label: 'Daily at noon',    value: '0 12 * * *' },
  { label: 'Weekly (Mon 8am)', value: '0 8 * * 1' },
  { label: 'Weekly (Sun midnight)', value: '0 0 * * 0' },
];

const AVAILABLE_FUNCTIONS: { slug: string; label: string; description: string }[] = [
  { slug: 'fetch-parks',           label: 'Fetch National Parks',     description: 'Import park listings from the NPS API' },
  { slug: 'geocode-events',         label: 'Geocode Events',           description: 'Geocode events that are missing coordinates' },
  { slug: 'fetch-serpapi-events',   label: 'Fetch Google Events',      description: 'Import events from SerpApi Google Events API' },
];

// ─── Helpers ───────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: RunLog['status'] }) {
  const map = {
    running:   { icon: Loader2,      label: 'Running',   cls: 'bg-blue-950/60 text-blue-300 border-blue-800/60',          spin: true  },
    success:   { icon: CheckCircle,  label: 'Success',   cls: 'bg-emerald-950/60 text-emerald-300 border-emerald-800/60', spin: false },
    error:     { icon: XCircle,      label: 'Error',     cls: 'bg-red-950/60 text-red-300 border-red-800/60',             spin: false },
    cancelled: { icon: AlertCircle,  label: 'Cancelled', cls: 'bg-slate-800/60 text-slate-400 border-slate-700/60',       spin: false },
  };
  const { icon: Icon, label, cls, spin } = map[status];
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${cls}`}>
      <Icon className={`w-3 h-3 ${spin ? 'animate-spin' : ''}`} />
      {label}
    </span>
  );
}

function EmptyState({ icon: Icon, title, body }: { icon: React.ElementType; title: string; body: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
      <div className="w-12 h-12 bg-slate-800 rounded-2xl flex items-center justify-center mb-4">
        <Icon className="w-6 h-6 text-slate-500" />
      </div>
      <p className="text-slate-300 font-medium text-sm mb-1">{title}</p>
      <p className="text-slate-500 text-sm">{body}</p>
    </div>
  );
}

// ─── Schedule Form ─────────────────────────────────────────────────────────

interface ScheduleFormProps {
  initial?: Partial<Schedule>;
  onSave: (data: Omit<Schedule, 'id' | 'created_at' | 'last_run_at' | 'next_run_at'>) => Promise<void>;
  onCancel: () => void;
}

// Functions that support per-schedule config options
const FUNCTION_CONFIG_FIELDS: Record<string, { key: string; label: string; placeholder: string; description: string }[]> = {
  'fetch-serpapi-events': [
    { key: 'location', label: 'Search locations', placeholder: 'Pittsburgh, PA; Robinson, PA; Cranberry, PA', description: 'Separate multiple locations with semicolons. Small towns automatically fall back to the nearest major city.' },
  ],
};

function ScheduleForm({ initial, onSave, onCancel }: ScheduleFormProps) {
  const [scheduleType, setScheduleType] = useState<ScheduleType>(initial?.schedule_type ?? 'agent');
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [prompt, setPrompt] = useState(initial?.prompt ?? '');
  const [functionSlug, setFunctionSlug] = useState(initial?.function_slug ?? AVAILABLE_FUNCTIONS[0].slug);
  const [cron, setCron] = useState(initial?.cron_expression ?? '0 */6 * * *');
  const [isActive, setIsActive] = useState(initial?.is_active ?? true);
  const [config, setConfig] = useState<Record<string, string>>(() => {
    const cfg = initial?.config ?? {};
    const fields = FUNCTION_CONFIG_FIELDS[initial?.function_slug ?? ''] ?? [];
    const result: Record<string, string> = {};
    for (const f of fields) {
      result[f.key] = String(cfg[f.key] ?? '');
    }
    return result;
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      const configObj: Record<string, string> = {};
      const fields = FUNCTION_CONFIG_FIELDS[functionSlug] ?? [];
      for (const f of fields) {
        if (config[f.key]) configObj[f.key] = config[f.key];
      }
      await onSave({
        name,
        description: description || null,
        prompt: scheduleType === 'agent' ? prompt : '',
        cron_expression: cron,
        is_active: isActive,
        schedule_type: scheduleType,
        function_slug: scheduleType === 'function' ? functionSlug : null,
        config: scheduleType === 'function' ? configObj : {},
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Type selector */}
      <div>
        <label className="block text-xs font-medium text-slate-400 mb-1.5">Schedule type</label>
        <div className="flex gap-2">
          {([['agent', Bot, 'AI Agent', 'Claude discovers events via a prompt'],
             ['function', Zap, 'Direct Function', 'Calls an edge function directly']] as const).map(
            ([type, Icon, label, hint]) => (
              <button
                key={type}
                type="button"
                onClick={() => setScheduleType(type)}
                className={`flex-1 flex items-start gap-3 px-3.5 py-3 rounded-xl border text-left transition-all ${
                  scheduleType === type
                    ? 'bg-blue-500/10 border-blue-500/50 text-white'
                    : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600'
                }`}
              >
                <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${scheduleType === type ? 'text-blue-400' : ''}`} />
                <div>
                  <p className="text-xs font-semibold">{label}</p>
                  <p className="text-[11px] text-slate-500 mt-0.5">{hint}</p>
                </div>
              </button>
            )
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <label className="block text-xs font-medium text-slate-400 mb-1.5">Schedule name</label>
          <input
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={scheduleType === 'agent' ? 'Pittsburgh Weekend Events' : 'Weekly Parks Refresh'}
            className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500/60 transition-all"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-xs font-medium text-slate-400 mb-1.5">Description <span className="text-slate-600">(optional)</span></label>
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={scheduleType === 'agent' ? 'Finds upcoming events in Pittsburgh PA' : 'Refreshes park data from NPS'}
            className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500/60 transition-all"
          />
        </div>
      </div>

      {scheduleType === 'agent' ? (
        <div>
          <label className="block text-xs font-medium text-slate-400 mb-1.5">Agent prompt</label>
          <textarea
            required
            rows={5}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Find upcoming events in Pittsburgh, PA for the next 2 weeks. Include concerts, festivals, sports, and community events. Return results as a JSON array."
            className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500/60 transition-all resize-none font-mono"
          />
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1.5">Function</label>
            <div className="space-y-2">
              {AVAILABLE_FUNCTIONS.map((fn) => (
                <label
                  key={fn.slug}
                  className={`flex items-start gap-3 px-3.5 py-3 rounded-xl border cursor-pointer transition-all ${
                    functionSlug === fn.slug
                      ? 'bg-blue-500/10 border-blue-500/50'
                      : 'bg-slate-800 border-slate-700 hover:border-slate-600'
                  }`}
                >
                  <input
                    type="radio"
                    name="functionSlug"
                    value={fn.slug}
                    checked={functionSlug === fn.slug}
                    onChange={() => {
                      setFunctionSlug(fn.slug);
                      const fields = FUNCTION_CONFIG_FIELDS[fn.slug] ?? [];
                      const newConfig: Record<string, string> = {};
                      for (const f of fields) newConfig[f.key] = '';
                      setConfig(newConfig);
                    }}
                    className="mt-1 accent-blue-500"
                  />
                  <div>
                    <p className="text-sm font-medium text-white">{fn.label}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{fn.description}</p>
                    <code className="text-[11px] text-slate-600 font-mono">{fn.slug}</code>
                  </div>
                </label>
              ))}
            </div>
          </div>
          {(FUNCTION_CONFIG_FIELDS[functionSlug] ?? []).map((f) => (
            <div key={f.key}>
              <label className="block text-xs font-medium text-slate-400 mb-1.5">{f.label}</label>
              <input
                value={config[f.key] ?? ''}
                onChange={(e) => setConfig((prev) => ({ ...prev, [f.key]: e.target.value }))}
                placeholder={f.placeholder}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500/60 transition-all"
              />
              <p className="text-slate-500 text-xs mt-1">{f.description}</p>
            </div>
          ))}
        </div>
      )}

      <div>
        <label className="block text-xs font-medium text-slate-400 mb-1.5">Schedule (cron)</label>
        <div className="flex flex-wrap gap-1.5 mb-2">
          {CRON_PRESETS.map((p) => (
            <button
              key={p.value}
              type="button"
              onClick={() => setCron(p.value)}
              className={`text-xs px-2.5 py-1 rounded-md border transition-all ${
                cron === p.value
                  ? 'bg-blue-500/20 border-blue-500/50 text-blue-300'
                  : 'bg-slate-800 border-slate-700 text-slate-400 hover:text-slate-200 hover:border-slate-600'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <input
          required
          value={cron}
          onChange={(e) => setCron(e.target.value)}
          placeholder="0 * * * *"
          className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500/60 transition-all"
        />
        <p className="text-slate-500 text-xs mt-1">Standard cron syntax: minute hour day month weekday</p>
      </div>

      <div className="flex items-center gap-3">
        <button
          type="button"
          role="switch"
          aria-checked={isActive}
          onClick={() => setIsActive(!isActive)}
          className={`relative w-9 h-5 rounded-full transition-colors ${isActive ? 'bg-blue-500' : 'bg-slate-700'}`}
        >
          <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${isActive ? 'translate-x-4' : ''}`} />
        </button>
        <span className="text-sm text-slate-300">{isActive ? 'Active' : 'Paused'}</span>
      </div>

      {error && (
        <div className="flex items-start gap-2 bg-red-950/50 border border-red-900/60 rounded-lg px-3 py-2.5">
          <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
          <p className="text-red-300 text-sm">{error}</p>
        </div>
      )}

      <div className="flex gap-3 pt-1">
        <button
          type="submit"
          disabled={saving}
          className="flex-1 bg-blue-500 hover:bg-blue-400 disabled:bg-blue-500/40 text-white font-medium py-2 rounded-lg text-sm transition-all flex items-center justify-center gap-2"
        >
          {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</> : (initial?.name ? 'Update schedule' : 'Create schedule')}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-sm transition-all"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

// ─── Log Row ───────────────────────────────────────────────────────────────

function LogRow({ log, onCancelled }: { log: RunLog; onCancelled: (id: string) => void }) {
  const [expanded, setExpanded] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  async function handleCancel(e: React.MouseEvent) {
    e.stopPropagation();
    setCancelling(true);
    await supabase
      .from('agent_run_logs')
      .update({ status: 'cancelled', completed_at: new Date().toISOString() })
      .eq('id', log.id);
    onCancelled(log.id);
    setCancelling(false);
  }

  return (
    <div className="border border-slate-800 rounded-xl overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-800/50 transition-colors text-left"
      >
        <StatusBadge status={log.status} />
        <div className="flex-1 min-w-0">
          <p className="text-slate-200 text-sm font-medium truncate">
            {log.schedule_name ?? 'Manual run'}
          </p>
          <p className="text-slate-500 text-xs">
            {formatDistanceToNow(new Date(log.started_at), { addSuffix: true })}
            {log.duration_ms != null && ` · ${(log.duration_ms / 1000).toFixed(1)}s`}
            {log.status === 'success' && ` · ${log.events_inserted} event${log.events_inserted !== 1 ? 's' : ''} inserted`}
          </p>
        </div>
        <span className="text-xs text-slate-600 bg-slate-800 px-2 py-0.5 rounded-full flex-shrink-0">
          {log.triggered_by}
        </span>
        {log.status === 'running' && (
          <button
            onClick={handleCancel}
            disabled={cancelling}
            className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-red-950/50 text-red-400 border border-red-800/50 hover:bg-red-900/50 transition-colors flex-shrink-0 disabled:opacity-50"
          >
            {cancelling ? <Loader2 className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3" />}
            Stop
          </button>
        )}
        {expanded ? <ChevronDown className="w-4 h-4 text-slate-500 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-500 flex-shrink-0" />}
      </button>

      {expanded && (
        <div className="border-t border-slate-800 bg-slate-950/50 px-4 py-3 space-y-3">
          {log.prompt && (
            <div>
              <p className="text-xs font-medium text-slate-500 mb-1">Prompt</p>
              <p className="text-slate-300 text-sm leading-relaxed">{log.prompt}</p>
            </div>
          )}
          {log.error_message && (
            <div>
              <p className="text-xs font-medium text-red-400 mb-1">Error</p>
              <p className="text-red-300 text-sm font-mono bg-red-950/30 rounded-lg px-3 py-2">{log.error_message}</p>
            </div>
          )}
          {log.output && (
            <div>
              <p className="text-xs font-medium text-slate-500 mb-1">Output</p>
              <pre className="text-xs text-slate-400 font-mono bg-slate-900 rounded-lg px-3 py-2 overflow-x-auto max-h-64 overflow-y-auto whitespace-pre-wrap break-all">
                {log.output}
              </pre>
            </div>
          )}
          <div className="flex gap-4 text-xs text-slate-600">
            <span>Started: {format(new Date(log.started_at), 'MMM d, yyyy HH:mm:ss')}</span>
            {log.completed_at && <span>Completed: {format(new Date(log.completed_at), 'HH:mm:ss')}</span>}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Dashboard ────────────────────────────────────────────────────────

type Tab = 'schedules' | 'logs' | 'messages' | 'submissions';

export default function AdminDashboard({ onLogout, userEmail }: AdminDashboardProps) {
  const [tab, setTab] = useState<Tab>('schedules');
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [logs, setLogs] = useState<RunLog[]>([]);
  const [messages, setMessages] = useState<ContactSubmission[]>([]);
  const [eventRequests, setEventRequests] = useState<EventRequest[]>([]);
  const [loadingSchedules, setLoadingSchedules] = useState(true);
  const [loadingLogs, setLoadingLogs] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(true);
  const [loadingSubmissions, setLoadingSubmissions] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editSchedule, setEditSchedule] = useState<Schedule | null>(null);
  const [runningId, setRunningId] = useState<string | null>(null);
  const [runError, setRunError] = useState<string | null>(null);
  const [manualPrompt, setManualPrompt] = useState('');
  const [showManualRun, setShowManualRun] = useState(false);
  const [manualRunning, setManualRunning] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [geocodeResult, setGeocodeResult] = useState<string | null>(null);

  const fetchSchedules = useCallback(async () => {
    setLoadingSchedules(true);
    const { data } = await supabase
      .from('agent_schedules')
      .select('*')
      .order('created_at', { ascending: false });
    setSchedules(data ?? []);
    setLoadingSchedules(false);
  }, []);

  const fetchLogs = useCallback(async (silent = false) => {
    if (!silent) setLoadingLogs(true);
    const { data } = await supabase
      .from('agent_run_logs')
      .select('*')
      .order('started_at', { ascending: false })
      .limit(100);
    setLogs(data ?? []);
    if (!silent) setLoadingLogs(false);
  }, []);

  const fetchMessages = useCallback(async () => {
    setLoadingMessages(true);
    const { data } = await supabase
      .from('contact_submissions')
      .select('*')
      .order('created_at', { ascending: false });
    setMessages(data ?? []);
    setLoadingMessages(false);
  }, []);

  const fetchEventRequests = useCallback(async () => {
    setLoadingSubmissions(true);
    const { data } = await supabase
      .from('event_requests')
      .select('*')
      .order('created_at', { ascending: false });
    setEventRequests(data ?? []);
    setLoadingSubmissions(false);
  }, []);

  useEffect(() => { fetchSchedules(); }, [fetchSchedules]);
  useEffect(() => { if (tab === 'logs') fetchLogs(); }, [tab, fetchLogs]);
  useEffect(() => { if (tab === 'messages') fetchMessages(); }, [tab, fetchMessages]);
  useEffect(() => { if (tab === 'submissions') fetchEventRequests(); }, [tab, fetchEventRequests]);

  useEffect(() => {
    if (tab !== 'logs') return;
    const hasRunning = logs.some((l) => l.status === 'running');
    if (!hasRunning) return;
    const id = setInterval(() => fetchLogs(true), 3000);
    return () => clearInterval(id);
  }, [tab, logs, fetchLogs]);

  const handleSaveSchedule = async (data: Omit<Schedule, 'id' | 'created_at' | 'last_run_at' | 'next_run_at'>) => {
    if (editSchedule) {
      await supabase.from('agent_schedules').update(data).eq('id', editSchedule.id);
    } else {
      await supabase.from('agent_schedules').insert(data);
    }
    setShowForm(false);
    setEditSchedule(null);
    fetchSchedules();
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this schedule? Run logs will be kept.')) return;
    await supabase.from('agent_schedules').delete().eq('id', id);
    fetchSchedules();
  };

  const handleToggleActive = async (s: Schedule) => {
    await supabase.from('agent_schedules').update({ is_active: !s.is_active }).eq('id', s.id);
    fetchSchedules();
  };

  // Trigger a direct edge-function schedule (non-agent)
  const triggerFunctionRun = useCallback(async (s: Schedule) => {
    if (!s.function_slug) return;
    setRunningId(s.id);
    setRunError(null);

    const { data: logRow } = await supabase
      .from('agent_run_logs')
      .insert({
        schedule_id: s.id,
        schedule_name: s.name,
        status: 'running',
        prompt: null,
        triggered_by: 'manual',
        started_at: new Date().toISOString(),
      })
      .select('id')
      .single();

    const logId = logRow?.id;
    const startedAt = Date.now();

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const resp = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${s.function_slug}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify(s.config ?? {}),
        }
      );
      const result = await resp.json();
      const eventsInserted = result.updated ?? result.inserted ?? result.events_inserted ?? 0;

      if (logId) {
        await supabase.from('agent_run_logs').update({
          status: resp.ok ? 'success' : 'error',
          output: JSON.stringify(result, null, 2),
          events_inserted: eventsInserted,
          error_message: resp.ok ? null : (result.error ?? 'Function returned an error'),
          duration_ms: Date.now() - startedAt,
          completed_at: new Date().toISOString(),
        }).eq('id', logId);
      }

      await supabase
        .from('agent_schedules')
        .update({ last_run_at: new Date().toISOString() })
        .eq('id', s.id);

      setTab('logs');
      fetchLogs();
      fetchSchedules();
    } catch (err) {
      if (logId) {
        await supabase.from('agent_run_logs').update({
          status: 'error',
          error_message: err instanceof Error ? err.message : String(err),
          duration_ms: Date.now() - startedAt,
          completed_at: new Date().toISOString(),
        }).eq('id', logId);
      }
      setRunError(err instanceof Error ? err.message : 'Run failed');
    } finally {
      setRunningId(null);
    }
  }, [fetchLogs, fetchSchedules]);

  // Trigger a Claude-agent schedule
  const triggerAgentRun = useCallback(async (s: Schedule) => {
    setRunningId(s.id);
    setRunError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const resp = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/run-claude-agent`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` },
          body: JSON.stringify({ scheduleId: s.id, prompt: s.prompt, triggeredBy: 'manual' }),
        }
      );
      const result = await resp.json();
      if (!result.success) throw new Error(result.error ?? 'Run failed');
      setTab('logs');
      fetchLogs();
      fetchSchedules();
    } catch (err) {
      setRunError(err instanceof Error ? err.message : 'Run failed');
    } finally {
      setRunningId(null);
    }
  }, [fetchLogs, fetchSchedules]);

  const triggerRun = useCallback((s: Schedule) => {
    if (s.schedule_type === 'function' && s.function_slug) {
      return triggerFunctionRun(s);
    }
    return triggerAgentRun(s);
  }, [triggerFunctionRun, triggerAgentRun]);

  const handleManualRun = async () => {
    if (!manualPrompt.trim()) return;
    setManualRunning(true);
    setRunError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const resp = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/run-claude-agent`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` },
          body: JSON.stringify({ prompt: manualPrompt, triggeredBy: 'manual' }),
        }
      );
      const result = await resp.json();
      if (!result.success) throw new Error(result.error ?? 'Run failed');
      setShowManualRun(false);
      setManualPrompt('');
      setTab('logs');
      fetchLogs();
    } catch (err) {
      setRunError(err instanceof Error ? err.message : 'Run failed');
    } finally {
      setManualRunning(false);
    }
  };

  const handleGeocodeNow = async () => {
    setGeocoding(true);
    setRunError(null);
    setGeocodeResult(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const resp = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/geocode-events`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` },
          body: JSON.stringify({}),
        }
      );
      const result = await resp.json();
      if (!resp.ok) throw new Error(result.error ?? 'Geocoding failed');
      const updated = result.updated ?? 0;
      const failed = result.failed ?? 0;
      const total = result.total ?? 0;
      setGeocodeResult(`Geocoded ${updated} of ${total} events (${failed} failed)`);
    } catch (err) {
      setRunError(err instanceof Error ? err.message : 'Geocoding failed');
    } finally {
      setGeocoding(false);
    }
  };

  const handleMarkRead = async (id: string, read: boolean) => {
    await supabase.from('contact_submissions').update({ read }).eq('id', id);
    setMessages((prev) => prev.map((m) => m.id === id ? { ...m, read } : m));
  };

  const handleDeleteMessage = async (id: string) => {
    await supabase.from('contact_submissions').delete().eq('id', id);
    setMessages((prev) => prev.filter((m) => m.id !== id));
  };

  const handleApproveEvent = async (req: EventRequest) => {
    const dedupHash = `user-submit-${req.id}`;
    const { error: insertError } = await supabase.from('events').insert({
      dedup_hash: dedupHash,
      title: req.title,
      description: req.description,
      start_datetime: req.start_datetime,
      end_datetime: req.end_datetime,
      venue_name: req.venue_name,
      address: req.address,
      city: req.city,
      state: req.state,
      zip_code: req.zip_code,
      url: req.url,
      image_url: req.image_url,
      price: req.price,
      categories: req.categories,
      source_domain: 'user-submission',
      extraction_method: 'manual_submit',
      confidence: 1.0,
      extracted_at: new Date().toISOString(),
    });
    if (insertError) {
      setRunError(`Failed to publish event: ${insertError.message}`);
      return;
    }
    await supabase.from('event_requests').update({
      status: 'approved',
      reviewed_at: new Date().toISOString(),
      reviewed_by: userEmail,
    }).eq('id', req.id);
    setEventRequests((prev) => prev.map((r) => r.id === req.id ? { ...r, status: 'approved', reviewed_at: new Date().toISOString(), reviewed_by: userEmail } : r));
  };

  const handleRejectEvent = async (req: EventRequest) => {
    await supabase.from('event_requests').update({
      status: 'rejected',
      reviewed_at: new Date().toISOString(),
      reviewed_by: userEmail,
    }).eq('id', req.id);
    setEventRequests((prev) => prev.map((r) => r.id === req.id ? { ...r, status: 'rejected', reviewed_at: new Date().toISOString(), reviewed_by: userEmail } : r));
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    onLogout();
  };

  const tabs: { id: Tab; label: string; icon: React.ElementType }[] = [
    { id: 'schedules', label: 'Schedules', icon: AlarmClock },
    { id: 'logs',      label: 'Run Logs',  icon: Activity },
    { id: 'messages',  label: 'Messages',  icon: Mail },
    { id: 'submissions', label: 'Submissions', icon: Inbox },
  ];

  const agentSchedules   = schedules.filter((s) => s.schedule_type === 'agent');
  const functionSchedules = schedules.filter((s) => s.schedule_type === 'function');

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col">
      {/* Top bar */}
      <header className="bg-slate-900 border-b border-slate-800 px-6 py-3 flex items-center gap-4 flex-shrink-0">
        <div className="flex items-center gap-2.5 flex-shrink-0">
          <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center shadow-md shadow-blue-500/30">
            <Calendar className="w-4 h-4 text-white" />
          </div>
          <div>
            <span className="text-white font-semibold text-sm tracking-tight">EventMap</span>
            <span className="text-slate-500 text-xs ml-2">Admin</span>
          </div>
        </div>

        <div className="flex-1" />

        <button
          onClick={handleGeocodeNow}
          disabled={geocoding}
          title="Geocode events that are missing coordinates"
          className="flex items-center gap-1.5 bg-slate-800 hover:bg-slate-700 disabled:bg-slate-800/60 border border-slate-700 text-slate-200 text-sm font-medium px-3.5 py-1.5 rounded-lg transition-all"
        >
          {geocoding ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
          {geocoding ? 'Geocoding...' : 'Geocode events'}
        </button>
        {geocodeResult && !geocoding && (
          <span className="text-xs text-emerald-400">{geocodeResult}</span>
        )}

        <button
          onClick={() => { setShowManualRun(true); setRunError(null); }}
          className="flex items-center gap-1.5 bg-blue-500 hover:bg-blue-400 text-white text-sm font-medium px-3.5 py-1.5 rounded-lg transition-all shadow-md shadow-blue-500/20"
        >
          <Bot className="w-4 h-4" />
          Run agent now
        </button>

        <div className="flex items-center gap-2 bg-slate-800 rounded-lg px-3 py-1.5">
          <div className="w-2 h-2 bg-emerald-400 rounded-full" />
          <span className="text-slate-300 text-xs">{userEmail}</span>
        </div>

        <button
          onClick={handleLogout}
          title="Sign out"
          className="p-1.5 text-slate-400 hover:text-slate-200 hover:bg-slate-800 rounded-lg transition-all"
        >
          <LogOut className="w-4 h-4" />
        </button>
      </header>

      {/* Manual run modal */}
      {showManualRun && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 w-full max-w-lg shadow-2xl">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center">
                  <Terminal className="w-4 h-4 text-blue-400" />
                </div>
                <div>
                  <h2 className="text-white font-semibold text-sm">Run agent now</h2>
                  <p className="text-slate-500 text-xs">One-shot manual trigger</p>
                </div>
              </div>
              <button onClick={() => setShowManualRun(false)} className="text-slate-500 hover:text-slate-300 transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            <textarea
              rows={6}
              value={manualPrompt}
              onChange={(e) => setManualPrompt(e.target.value)}
              placeholder="Find upcoming events in Pittsburgh, PA for the next 2 weeks..."
              className="w-full px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500/60 transition-all resize-none"
            />
            {runError && (
              <div className="mt-3 flex items-start gap-2 bg-red-950/50 border border-red-900/60 rounded-lg px-3 py-2.5">
                <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-red-300 text-sm">{runError}</p>
              </div>
            )}
            <div className="flex gap-3 mt-4">
              <button
                onClick={handleManualRun}
                disabled={manualRunning || !manualPrompt.trim()}
                className="flex-1 bg-blue-500 hover:bg-blue-400 disabled:bg-blue-500/40 disabled:cursor-not-allowed text-white font-medium py-2 rounded-lg text-sm transition-all flex items-center justify-center gap-2"
              >
                {manualRunning ? <><Loader2 className="w-4 h-4 animate-spin" /> Running...</> : <><Play className="w-4 h-4" /> Run agent</>}
              </button>
              <button
                onClick={() => setShowManualRun(false)}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-sm transition-all"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-slate-800 px-6 flex gap-1 bg-slate-900/50">
        {tabs.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-all -mb-px ${
              tab === id
                ? 'border-blue-500 text-blue-400'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
            {id === 'logs' && logs.some((l) => l.status === 'running') && (
              <span className="w-2 h-2 bg-blue-400 rounded-full animate-pulse" />
            )}
            {id === 'messages' && messages.filter((m) => !m.read).length > 0 && (
              <span className="min-w-[18px] h-[18px] px-1 bg-amber-500 text-zinc-900 text-[10px] font-bold rounded-full flex items-center justify-center">
                {messages.filter((m) => !m.read).length}
              </span>
            )}
            {id === 'submissions' && eventRequests.filter((r) => r.status === 'pending').length > 0 && (
              <span className="min-w-[18px] h-[18px] px-1 bg-amber-500 text-zinc-900 text-[10px] font-bold rounded-full flex items-center justify-center">
                {eventRequests.filter((r) => r.status === 'pending').length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-4xl mx-auto px-6 py-6">

          {/* SCHEDULES TAB */}
          {tab === 'schedules' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-white font-semibold text-base">Schedules</h2>
                  <p className="text-slate-500 text-sm">Configure automated agents and data source refreshes</p>
                </div>
                {!showForm && (
                  <button
                    onClick={() => { setShowForm(true); setEditSchedule(null); }}
                    className="flex items-center gap-1.5 bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-200 text-sm font-medium px-3.5 py-1.5 rounded-lg transition-all"
                  >
                    <Plus className="w-4 h-4" />
                    New schedule
                  </button>
                )}
              </div>

              {showForm && (
                <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
                  <h3 className="text-white font-semibold text-sm mb-4">
                    {editSchedule ? 'Edit schedule' : 'New schedule'}
                  </h3>
                  <ScheduleForm
                    initial={editSchedule ?? undefined}
                    onSave={handleSaveSchedule}
                    onCancel={() => { setShowForm(false); setEditSchedule(null); }}
                  />
                </div>
              )}

              {runError && !showManualRun && (
                <div className="flex items-start gap-2 bg-red-950/50 border border-red-900/60 rounded-xl px-4 py-3">
                  <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-red-300 text-sm">{runError}</p>
                </div>
              )}

              {loadingSchedules ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="w-6 h-6 text-slate-500 animate-spin" />
                </div>
              ) : schedules.length === 0 ? (
                <div className="bg-slate-900 border border-slate-800 rounded-2xl">
                  <EmptyState
                    icon={AlarmClock}
                    title="No schedules yet"
                    body="Create a schedule to have the Claude agent automatically discover and import events."
                  />
                </div>
              ) : (
                <>
                  {/* Data Sources */}
                  {functionSchedules.length > 0 && (
                    <section className="space-y-3">
                      <div className="flex items-center gap-2">
                        <Database className="w-4 h-4 text-slate-500" />
                        <h3 className="text-slate-400 text-xs font-semibold uppercase tracking-wider">Data Sources</h3>
                      </div>
                      {functionSchedules.map((s) => (
                        <ScheduleCard
                          key={s.id}
                          schedule={s}
                          runningId={runningId}
                          onRun={triggerRun}
                          onToggle={handleToggleActive}
                          onEdit={() => { setEditSchedule(s); setShowForm(true); }}
                          onDelete={handleDelete}
                        />
                      ))}
                    </section>
                  )}

                  {/* Agent Schedules */}
                  {agentSchedules.length > 0 && (
                    <section className="space-y-3">
                      <div className="flex items-center gap-2">
                        <Bot className="w-4 h-4 text-slate-500" />
                        <h3 className="text-slate-400 text-xs font-semibold uppercase tracking-wider">AI Agent</h3>
                      </div>
                      {agentSchedules.map((s) => (
                        <ScheduleCard
                          key={s.id}
                          schedule={s}
                          runningId={runningId}
                          onRun={triggerRun}
                          onToggle={handleToggleActive}
                          onEdit={() => { setEditSchedule(s); setShowForm(true); }}
                          onDelete={handleDelete}
                        />
                      ))}
                    </section>
                  )}
                </>
              )}
            </div>
          )}

          {/* LOGS TAB */}
          {tab === 'logs' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-white font-semibold text-base">Run Logs</h2>
                  <p className="text-slate-500 text-sm">History of all runs — last 100 entries</p>
                </div>
                <button
                  onClick={() => fetchLogs()}
                  className="flex items-center gap-1.5 bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-300 text-sm px-3.5 py-1.5 rounded-lg transition-all"
                >
                  <RefreshCw className="w-4 h-4" />
                  Refresh
                </button>
              </div>

              {loadingLogs ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="w-6 h-6 text-slate-500 animate-spin" />
                </div>
              ) : logs.length === 0 ? (
                <div className="bg-slate-900 border border-slate-800 rounded-2xl">
                  <EmptyState
                    icon={Activity}
                    title="No runs yet"
                    body="Run a schedule or trigger a manual run to see logs here."
                  />
                </div>
              ) : (
                <div className="space-y-2">
                  {logs.map((log) => (
                    <LogRow
                      key={log.id}
                      log={log}
                      onCancelled={(id) =>
                        setLogs((prev) =>
                          prev.map((l) =>
                            l.id === id ? { ...l, status: 'cancelled', completed_at: new Date().toISOString() } : l
                          )
                        )
                      }
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* MESSAGES TAB */}
          {tab === 'messages' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-white font-semibold text-base">Messages</h2>
                  <p className="text-slate-500 text-sm">Contact form submissions from visitors</p>
                </div>
                <button
                  onClick={fetchMessages}
                  className="flex items-center gap-1.5 bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-300 text-sm px-3.5 py-1.5 rounded-lg transition-all"
                >
                  <RefreshCw className="w-4 h-4" />
                  Refresh
                </button>
              </div>

              {loadingMessages ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="w-6 h-6 text-slate-500 animate-spin" />
                </div>
              ) : messages.length === 0 ? (
                <div className="bg-slate-900 border border-slate-800 rounded-2xl">
                  <EmptyState
                    icon={Mail}
                    title="No messages yet"
                    body="Contact form submissions will appear here."
                  />
                </div>
              ) : (
                <div className="space-y-3">
                  {messages.map((m) => (
                    <MessageRow
                      key={m.id}
                      message={m}
                      onMarkRead={handleMarkRead}
                      onDelete={handleDeleteMessage}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* SUBMISSIONS TAB */}
          {tab === 'submissions' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-white font-semibold text-base">Event Submissions</h2>
                  <p className="text-slate-500 text-sm">User-submitted events awaiting review</p>
                </div>
                <button
                  onClick={fetchEventRequests}
                  className="flex items-center gap-1.5 bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-300 text-sm px-3.5 py-1.5 rounded-lg transition-all"
                >
                  <RefreshCw className="w-4 h-4" />
                  Refresh
                </button>
              </div>

              {runError && tab === 'submissions' && (
                <div className="flex items-start gap-2 bg-red-950/50 border border-red-900/60 rounded-xl px-4 py-3">
                  <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-red-300 text-sm">{runError}</p>
                </div>
              )}

              {loadingSubmissions ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="w-6 h-6 text-slate-500 animate-spin" />
                </div>
              ) : eventRequests.length === 0 ? (
                <div className="bg-slate-900 border border-slate-800 rounded-2xl">
                  <EmptyState
                    icon={Inbox}
                    title="No submissions yet"
                    body="Event submissions from visitors will appear here for review."
                  />
                </div>
              ) : (
                <div className="space-y-3">
                  {eventRequests.map((req) => (
                    <EventRequestRow
                      key={req.id}
                      request={req}
                      onApprove={handleApproveEvent}
                      onReject={handleRejectEvent}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

// ─── Event Request Row ─────────────────────────────────────────────────────

function EventRequestRow({
  request: req,
  onApprove,
  onReject,
}: {
  request: EventRequest;
  onApprove: (req: EventRequest) => void;
  onReject: (req: EventRequest) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [approving, setApproving] = useState(false);

  const statusMap = {
    pending:  { label: 'Pending',  cls: 'bg-amber-950/60 text-amber-300 border-amber-800/60' },
    approved: { label: 'Approved', cls: 'bg-emerald-950/60 text-emerald-300 border-emerald-800/60' },
    rejected: { label: 'Rejected', cls: 'bg-red-950/60 text-red-300 border-red-800/60' },
  } as const;
  const status = statusMap[req.status];

  const fmtDate = (dt: string | null) => {
    if (!dt) return null;
    try { return format(new Date(dt), 'MMM d, yyyy h:mm a'); } catch { return dt; }
  };

  const handleApprove = async () => {
    setApproving(true);
    await onApprove(req);
    setApproving(false);
  };

  return (
    <div className={`border rounded-xl overflow-hidden transition-all ${
      req.status === 'pending' ? 'border-amber-800/50 bg-amber-950/5' : 'border-slate-800'
    }`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-800/50 transition-colors text-left"
      >
        <div className="flex-shrink-0">
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${status.cls}`}>
            {status.label}
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium truncate ${req.status === 'pending' ? 'text-white' : 'text-slate-300'}`}>
            {req.title}
          </p>
          <p className="text-slate-500 text-xs truncate">
            {fmtDate(req.start_datetime) || 'No date'}
            {req.venue_name ? ` · ${req.venue_name}` : ''}
            {req.city ? `, ${req.city}` : ''}
          </p>
        </div>
        <span className="text-xs text-slate-600 flex-shrink-0">
          {formatDistanceToNow(new Date(req.created_at), { addSuffix: true })}
        </span>
        {expanded
          ? <ChevronDown className="w-4 h-4 text-slate-500 flex-shrink-0" />
          : <ChevronRight className="w-4 h-4 text-slate-500 flex-shrink-0" />}
      </button>

      {expanded && (
        <div className="border-t border-slate-800 bg-slate-950/50 px-4 py-3 space-y-3">
          {req.description && (
            <div>
              <p className="text-xs font-medium text-slate-500 mb-1">Description</p>
              <p className="text-slate-300 text-sm leading-relaxed whitespace-pre-wrap">{req.description}</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
            {req.start_datetime && (
              <div><span className="text-slate-500">Start:</span> <span className="text-slate-300">{fmtDate(req.start_datetime)}</span></div>
            )}
            {req.end_datetime && (
              <div><span className="text-slate-500">End:</span> <span className="text-slate-300">{fmtDate(req.end_datetime)}</span></div>
            )}
            {req.venue_name && (
              <div><span className="text-slate-500">Venue:</span> <span className="text-slate-300">{req.venue_name}</span></div>
            )}
            {req.address && (
              <div><span className="text-slate-500">Address:</span> <span className="text-slate-300">{req.address}</span></div>
            )}
            {req.city && (
              <div><span className="text-slate-500">City:</span> <span className="text-slate-300">{req.city}</span></div>
            )}
            {req.state && (
              <div><span className="text-slate-500">State:</span> <span className="text-slate-300">{req.state}</span></div>
            )}
            {req.zip_code && (
              <div><span className="text-slate-500">ZIP:</span> <span className="text-slate-300">{req.zip_code}</span></div>
            )}
            {req.price && (
              <div><span className="text-slate-500">Price:</span> <span className="text-slate-300">{req.price}</span></div>
            )}
          </div>

          {req.categories && req.categories.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {req.categories.map((cat) => (
                <span key={cat} className="inline-flex items-center gap-0.5 text-[10px] font-medium text-slate-400 bg-slate-800 px-2 py-0.5 rounded-md">
                  <Tag className="w-2.5 h-2.5" />
                  {cat}
                </span>
              ))}
            </div>
          )}

          <div className="flex flex-wrap gap-3 text-xs text-slate-500">
            {req.url && (
              <a href={req.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-blue-400 hover:text-blue-300 transition-colors">
                <ExternalLink className="w-3 h-3" />
                Event link
              </a>
            )}
            {req.image_url && (
              <a href={req.image_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-blue-400 hover:text-blue-300 transition-colors">
                <ExternalLink className="w-3 h-3" />
                Image
              </a>
            )}
            {req.submitter_name && (
              <span>Submitted by: {req.submitter_name}{req.submitter_email ? ` (${req.submitter_email})` : ''}</span>
            )}
          </div>

          {req.status === 'pending' && (
            <div className="flex gap-2 pt-2">
              <button
                onClick={handleApprove}
                disabled={approving}
                className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-medium bg-emerald-600 hover:bg-emerald-500 text-white transition-all disabled:opacity-50"
              >
                {approving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                Approve & Publish
              </button>
              <button
                onClick={() => onReject(req)}
                className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-medium bg-slate-800 hover:bg-red-900/50 text-slate-400 hover:text-red-400 border border-slate-700 transition-all"
              >
                <XIcon className="w-3.5 h-3.5" />
                Reject
              </button>
            </div>
          )}

          {req.reviewed_at && (
            <p className="text-xs text-slate-600">
              {req.status === 'approved' ? 'Approved' : 'Rejected'} by {req.reviewed_by ?? 'admin'} {formatDistanceToNow(new Date(req.reviewed_at), { addSuffix: true })}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Message Row ───────────────────────────────────────────────────────────

function MessageRow({
  message: m,
  onMarkRead,
  onDelete,
}: {
  message: ContactSubmission;
  onMarkRead: (id: string, read: boolean) => void;
  onDelete: (id: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);

  const SUBJECT_LABELS: Record<string, string> = {
    report:   'Report inaccurate listing',
    suggest:  'Suggest an event',
    general:  'General question',
    feedback: 'Feedback',
    other:    'Other',
  };

  return (
    <div className={`border rounded-xl overflow-hidden transition-all ${m.read ? 'border-slate-800' : 'border-amber-800/50 bg-amber-950/10'}`}>
      <button
        onClick={() => {
          setExpanded(!expanded);
          if (!m.read) onMarkRead(m.id, true);
        }}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-800/50 transition-colors text-left"
      >
        <div className="flex-shrink-0 text-slate-500">
          {m.read
            ? <MailOpen className="w-4 h-4" />
            : <Mail className="w-4 h-4 text-amber-400" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className={`text-sm font-medium truncate ${m.read ? 'text-slate-300' : 'text-white'}`}>
              {m.name}
            </p>
            <span className="text-xs text-slate-500 truncate">{m.email}</span>
          </div>
          <p className="text-slate-500 text-xs truncate">
            {SUBJECT_LABELS[m.subject] ?? m.subject}
          </p>
        </div>
        <span className="text-xs text-slate-600 flex-shrink-0">
          {formatDistanceToNow(new Date(m.created_at), { addSuffix: true })}
        </span>
        {expanded
          ? <ChevronDown className="w-4 h-4 text-slate-500 flex-shrink-0" />
          : <ChevronRight className="w-4 h-4 text-slate-500 flex-shrink-0" />}
      </button>

      {expanded && (
        <div className="border-t border-slate-800 bg-slate-950/50 px-4 py-3 space-y-3">
          <p className="text-slate-300 text-sm leading-relaxed whitespace-pre-wrap">{m.message}</p>
          <div className="flex items-center gap-3 pt-1">
            <button
              onClick={() => onMarkRead(m.id, !m.read)}
              className="text-xs text-slate-400 hover:text-slate-200 flex items-center gap-1.5 transition-colors"
            >
              {m.read ? <Mail className="w-3.5 h-3.5" /> : <MailOpen className="w-3.5 h-3.5" />}
              Mark as {m.read ? 'unread' : 'read'}
            </button>
            <a
              href={`mailto:${m.email}?subject=Re: ${encodeURIComponent(SUBJECT_LABELS[m.subject] ?? m.subject)}`}
              className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1.5 transition-colors"
            >
              <Mail className="w-3.5 h-3.5" />
              Reply via email
            </a>
            <button
              onClick={() => onDelete(m.id)}
              className="ml-auto text-xs text-slate-600 hover:text-red-400 flex items-center gap-1.5 transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Delete
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Schedule Card ─────────────────────────────────────────────────────────

interface ScheduleCardProps {
  schedule: Schedule;
  runningId: string | null;
  onRun: (s: Schedule) => void;
  onToggle: (s: Schedule) => void;
  onEdit: () => void;
  onDelete: (id: string) => void;
}

function ScheduleCard({ schedule: s, runningId, onRun, onToggle, onEdit, onDelete }: ScheduleCardProps) {
  const isFunction = s.schedule_type === 'function';
  const fnMeta = isFunction ? AVAILABLE_FUNCTIONS.find((f) => f.slug === s.function_slug) : null;

  return (
    <div
      className={`bg-slate-900 border rounded-2xl p-5 transition-all ${
        s.is_active ? 'border-slate-800' : 'border-slate-800/50 opacity-60'
      }`}
    >
      <div className="flex items-start gap-4">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5 ${
          s.is_active
            ? isFunction ? 'bg-emerald-500/15' : 'bg-blue-500/15'
            : 'bg-slate-800'
        }`}>
          {isFunction
            ? <Zap className={`w-4 h-4 ${s.is_active ? 'text-emerald-400' : 'text-slate-500'}`} />
            : <Bot className={`w-4 h-4 ${s.is_active ? 'text-blue-400' : 'text-slate-500'}`} />}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-white font-semibold text-sm">{s.name}</h3>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
              s.is_active
                ? 'bg-emerald-950/60 text-emerald-400 border border-emerald-900/50'
                : 'bg-slate-800 text-slate-500 border border-slate-700'
            }`}>
              {s.is_active ? 'Active' : 'Paused'}
            </span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
              isFunction
                ? 'bg-slate-800 text-slate-400 border border-slate-700'
                : 'bg-slate-800 text-slate-400 border border-slate-700'
            }`}>
              {isFunction ? 'function' : 'agent'}
            </span>
          </div>

          {s.description && (
            <p className="text-slate-400 text-xs mt-0.5">{s.description}</p>
          )}

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2">
            <span className="flex items-center gap-1.5 text-xs text-slate-500">
              <Clock className="w-3.5 h-3.5" />
              <code className="font-mono">{s.cron_expression}</code>
            </span>
            {s.last_run_at && (
              <span className="text-xs text-slate-500">
                Last run {formatDistanceToNow(new Date(s.last_run_at), { addSuffix: true })}
              </span>
            )}
          </div>

          {isFunction && fnMeta ? (
            <div className="mt-2 space-y-0.5">
              <p className="text-slate-600 text-xs font-mono truncate">
                → {fnMeta.slug}
              </p>
              {s.config?.location ? (
                <p className="text-slate-500 text-xs flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {String(s.config.location)}
                </p>
              ) : null}
            </div>
          ) : s.prompt ? (
            <p className="text-slate-600 text-xs mt-2 font-mono truncate">{s.prompt}</p>
          ) : null}
        </div>

        <div className="flex items-center gap-1.5 flex-shrink-0">
          <button
            onClick={() => onRun(s)}
            disabled={runningId === s.id}
            title="Run now"
            className="p-2 bg-slate-800 hover:bg-blue-500/20 hover:text-blue-400 text-slate-400 rounded-lg transition-all disabled:opacity-50"
          >
            {runningId === s.id
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <Play className="w-4 h-4" />}
          </button>
          <button
            onClick={() => onToggle(s)}
            title={s.is_active ? 'Pause' : 'Resume'}
            className="p-2 bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-slate-200 rounded-lg transition-all"
          >
            {s.is_active ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </button>
          <button
            onClick={onEdit}
            title="Edit"
            className="p-2 bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-slate-200 rounded-lg transition-all"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            onClick={() => onDelete(s.id)}
            title="Delete"
            className="p-2 bg-slate-800 hover:bg-red-900/40 text-slate-400 hover:text-red-400 rounded-lg transition-all"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
